import { Database } from "@punchup/data/supabase.types";
import { SubscriptionStatusResponseEnum } from "@/app/(public)/api/check-subscription/subscription-response.types";
import { SupabaseClient } from "@supabase/supabase-js";
import { createSupabaseAdminClient } from "@/data/supabase";
import { getComedianByID } from "./comedians";
import { getHostname } from "@punchup/utils/hostname";
import { getProducts } from "./v2/client/public/products";
import { getUser } from "./supabase";
import { getUserPaidSubscriptions } from "./v2/client/user/paid-subscriptions";
import { logException } from "@punchup/sentry";

export interface Subscription {
  comedian_id: string;
  created_at: string;
  id: string;
  price_id: string;
  tier: number;
}

export type UserSubscriptionRow =
  Database["public"]["Tables"]["user_subscriptions"]["Row"];
export type StripeProductRow =
  Database["public"]["Tables"]["stripe_products"]["Row"];

export const stripeProductsTable =
  process.env.VERCEL_ENV === "production"
    ? "stripe_products"
    : "stripe_products_dev";

export const subscriptionsTable =
  process.env.VERCEL_ENV === "production"
    ? "user_subscriptions"
    : "user_subscriptions_dev";

export async function getStripeProductById(
  supabase: SupabaseClient<Database>,
  productId: string
): Promise<StripeProductRow | null> {
  const { data, error } = await supabase
    .from(stripeProductsTable)
    .select("*")
    .eq("id", productId)
    .maybeSingle();
  if (error) {
    console.error(`Database error retrieving product ${productId}:`, error);
    throw new Error(`Database error: ${error.message}`);
  }
  return data;
}

export async function getComedianStripeProducts(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<Subscription[]> {
  if (!comedianId) {
    console.error("getComedianStripeProducts called with empty comedianId");
    throw new Error("Comedian ID is required");
  }

  try {
    const { data, error } = await supabase
      .from(stripeProductsTable)
      .select("*")
      .eq("comedian_id", comedianId);

    if (error) {
      console.error(
        `Database error retrieving products for comedian ${comedianId}:`,
        error
      );
      throw new Error(`Database error: ${error.message}`);
    }

    if (data) {
      return data;
    }

    return [];
  } catch (error) {
    console.error(
      `Unexpected error in getComedianStripeProducts for comedian ${comedianId}:`,
      error
    );
    throw new Error(
      `Issue getting stripe products for comedian ${comedianId}: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

// Checks if a user is subscribed to a comedian and the status of the subscription
export async function userIsSubscribedToComedian(
  supabase: SupabaseClient<Database>,
  comedianId: string
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) {
    return false;
  }

  try {
    const subscriptionProductData = await getComedianStripeProducts(
      supabase,
      comedianId
    );

    // Case in which the comedian doesn't have any products
    if (!subscriptionProductData.length) {
      return false;
    }

    // Retrieve subscription status for this user
    const hostname = getHostname();
    const subscriptionStatusCheckResponse = await fetch(
      `${hostname}/api/check-subscription`,
      {
        method: "POST",
        body: JSON.stringify({
          productIdReference: subscriptionProductData[0].id,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (subscriptionStatusCheckResponse.ok) {
      const subscriptionStatusCheck =
        await subscriptionStatusCheckResponse.json();
      if (
        subscriptionStatusCheck.message ===
        SubscriptionStatusResponseEnum.SUBSCRIPTION_VALID
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      throw new Error(await subscriptionStatusCheckResponse.text());
    }
  } catch (err) {
    console.error(err);
    throw new Error(`Error while validating subscription status: ${err}`);
  }
}

export type SubscriptionWithComedian = {
  stripe_subscription_id: string;
  stripe_product: {
    price_id: string;
    tier: number;
    comedian: {
      id: string;
      display_name: string | null;
      slug: string;
      about_img: string | null;
    } | null;
  } | null;
};

export async function getUserSubscriptionsAndTheirComediansClient(
  supabase: SupabaseClient<Database>
): Promise<SubscriptionWithComedian[]> {
  const user = await getUser(supabase);
  if (!user) {
    return [];
  }

  const subscriptions = await getUserPaidSubscriptions({
    active: true,
  });
  return Promise.all(
    subscriptions.map(async (subscription) => {
      const comedian = await getComedianByID(subscription.comedian_id);
      if (!comedian) {
        throw new Error(
          "failed to fetch comedian in getUserSubscriptionsAndTheirComediansClient"
        );
      }
      const { slug, display_name, about_img } = comedian;
      const products = await getProducts({ comedianId: comedian.id });

      return {
        stripe_subscription_id: subscription.external_subscription_id,
        stripe_product: {
          price_id: products.at(0)?.stripe_price_id,
          tier: 1,
          comedian: {
            id: comedian.id,
            slug,
            display_name,
            about_img,
          },
        },
      } as SubscriptionWithComedian;
    })
  );
}
export async function getUserSubscriptionId(
  userId: string,
  stripeProductIdReference: string
) {
  const supabaseAdmin = createSupabaseAdminClient();

  const { data, error } = await supabaseAdmin
    .from(subscriptionsTable)
    .select("stripe_subscription_id")
    .match({ user_id: userId, stripe_product_id: stripeProductIdReference })
    .maybeSingle();

  if (error) {
    console.error(error);
  } else if (data) {
    return data.stripe_subscription_id;
  } else if (!error && !data) {
    return "";
  }
  throw new Error(
    `Error attemping to fetch subscription id for user ${userId}`
  );
}

export async function addSubscriptionToUser(
  supabaseAdmin: SupabaseClient<Database>,
  userID: string,
  subscriptionProductID: string,
  stripeSubscriptionID: string
) {
  if (!stripeSubscriptionID) {
    `Error attemping to add subscription because to user because it's missing the stripeSubscriptionID`;
  }

  if (!userID) {
    `Error attemping to add subscription ${stripeSubscriptionID} to user because userID is empty`;
  }

  if (!subscriptionProductID) {
    `Error attemping to add subscription ${stripeSubscriptionID} to user because subscriptionProductID is empty`;
  }

  const { status, error } = await supabaseAdmin
    .from(subscriptionsTable)
    .upsert(
      {
        user_id: userID,
        stripe_product_id: subscriptionProductID,
        stripe_subscription_id: stripeSubscriptionID,
      },
      {
        onConflict: "stripe_subscription_id",
        ignoreDuplicates: false,
        count: "exact",
      }
    )
    .select();

  if (error) {
    console.error(error);
  } else if (status === 201) {
    return;
  }
  throw new Error(
    `Error attemping to add subscription ${stripeSubscriptionID} to user ${userID}`
  );
}

// Note: this function is called (and should only be called) by a server function
// Never on the client
export async function removeSubscriptionForUser(
  userID: string,
  stripeSubscriptionID: string
) {
  if (!userID) {
    throw new Error(
      "Cannot proceed with purging subscription as the userID is missing"
    );
  }

  if (!stripeSubscriptionID) {
    throw new Error(
      "Cannot proceed with purging subscription as the stripeSubscriptionID is missing"
    );
  }
  const supabaseAdmin = createSupabaseAdminClient();
  const { count, status, error } = await supabaseAdmin
    .from(subscriptionsTable)
    .delete({ count: "exact" })
    .match({
      user_id: userID,
      stripe_subscription_id: stripeSubscriptionID,
    });
  if (error) {
    console.error(error);
    return { error: error };
  } else if (status === 204 && count === 1) {
    return true;
  } else {
    throw new Error(
      `Error deleting subscription for user with id ${userID}. Row count was: ${count}.`
    );
  }
}

export async function getSubscriptionPriceForComedian(
  supabase: SupabaseClient<Database>,
  comedianId: string
) {
  try {
    const { data } = await supabase
      .from("comedian_subscriptions")
      .select("price")
      .eq("comedian_id", comedianId)
      .maybeSingle()
      .throwOnError();

    return data?.price;
  } catch (error) {
    logException(error);
    return null;
  }
}
